import { useState } from "react";
import { IUpdateRewardThresholdsRequest, RewardThresholdType } from "api/models/request/IUpdateRewardThresholdsRequest";

export const REWARD_LEVELS = ["gold", "silver", "bronze"] as const;
export type RewardLevel = typeof REWARD_LEVELS[number];
export type ThresholdField = `${RewardLevel}ThresholdValue`;
type ThresholdDraft = Record<ThresholdField, string> & { thresholdType: RewardThresholdType };

const MAX_PERCENTAGE = 100;
const MAX_POINTS = 2147483647;

const toDraft = (saved: IUpdateRewardThresholdsRequest): ThresholdDraft => ({
    goldThresholdValue: String(saved.goldThresholdValue),
    silverThresholdValue: String(saved.silverThresholdValue),
    bronzeThresholdValue: String(saved.bronzeThresholdValue),
    thresholdType: saved.thresholdType,
});

export const useRewardThresholdsForm = (saved: IUpdateRewardThresholdsRequest | undefined) => {
    const [draft, setDraft] = useState<ThresholdDraft | null>(null);

    const values = draft ?? (saved ? toDraft(saved) : null);
    const percentage = values?.thresholdType === RewardThresholdType.Percentage;
    const maximum = percentage ? MAX_PERCENTAGE : MAX_POINTS;

    const isInvalidValue = (value: string) =>
        value.trim() === "" || !Number.isInteger(Number(value)) || Number(value) < 0 || Number(value) > maximum;

    const validNumbers = !!values && REWARD_LEVELS.every(level => !isInvalidValue(values[`${level}ThresholdValue`]));
    const validGoldOrder = !!values && Number(values.goldThresholdValue) > Number(values.silverThresholdValue);
    const validSilverOrder = !!values && Number(values.silverThresholdValue) > Number(values.bronzeThresholdValue);
    const validOrder = validGoldOrder && validSilverOrder;

    const setThresholdType = (thresholdType: RewardThresholdType) => {
        if (values) setDraft({ ...values, thresholdType });
    };

    const setFieldValue = (field: ThresholdField, value: string) => {
        if (values) setDraft({ ...values, [field]: value });
    };

    const resetDraft = () => setDraft(null);

    const toRequest = (): IUpdateRewardThresholdsRequest | null => values && {
        goldThresholdValue: Number(values.goldThresholdValue),
        silverThresholdValue: Number(values.silverThresholdValue),
        bronzeThresholdValue: Number(values.bronzeThresholdValue),
        thresholdType: values.thresholdType,
    };

    return {
        values,
        percentage,
        maximum,
        isInvalidValue,
        validNumbers,
        validGoldOrder,
        validSilverOrder,
        valid: validNumbers && validOrder,
        setThresholdType,
        setFieldValue,
        resetDraft,
        toRequest,
    };
};
